import { useEffect, useState } from 'react';
import { Card, CardContent, Grid, Stack, Typography, Box } from '@mui/material';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HubIcon from '@mui/icons-material/Hub';
import DevicesIcon from '@mui/icons-material/Devices';
import { useApiContext } from '@/contexts/apiContext';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '@/components/errorMessage';
import EntityLlmUsages from '@/components/entities/administration/usageStats/llmUsages';

const StatCard = ({ title, value, icon, subtitle }: any) => (
  <Card
    variant="outlined"
    sx={{
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      justifyContent: 'flex-start',
      borderRadius: '12px',
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      boxShadow: 'none',
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          bgcolor: 'rgba(0,0,0,0.03)',
          p: 0.75,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          '& .MuiSvgIcon-root': { fontSize: 18, color: 'secondary.main' },
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          fontSize: '0.65rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {title}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: subtitle ? 0.25 : 0, lineHeight: 1 }}>
      {value}
    </Typography>
    {subtitle && (
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.625rem', mt: 0.5 }}>
        {subtitle}
      </Typography>
    )}
  </Card>
);

type Props = {
  agentInstance: any;
  maxHeight?: number;
};

const usageCache: Record<string, any> = {};
const fetchGuard: Record<string, boolean> = {};

const AgentUsageStats = ({ agentInstance }: Props) => {
  const { t } = useTranslation();
  const apiContext = useApiContext();

  const [agentStats, setAgentStats] = useState<any>(usageCache[agentInstance?.id]);
  const [loading, setLoading] = useState<boolean>(!usageCache[agentInstance?.id]);
  const [error, setError] = useState<any>();

  useEffect(() => {
    const getAgentUsageStats = async () => {
      if (!agentInstance?.id || fetchGuard[agentInstance.id]) return;
      fetchGuard[agentInstance.id] = true;

      setError(null);
      if (!usageCache[agentInstance.id]) {
        setLoading(true);
      }

      try {
        const stats = await apiContext.getAgentInstanceUsageStats(agentInstance.agentDefId, agentInstance.id);
        usageCache[agentInstance.id] = stats;
        setAgentStats(stats);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
        fetchGuard[agentInstance.id] = false;
      }
    };

    if (agentInstance?.id) {
      getAgentUsageStats();
    }
  }, [agentInstance.id, agentInstance.agentDefId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', p: 0.5 }}>
      {error && <ErrorMessage error={error} />}

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Total Users" value={agentStats?.users || 0} icon={<PeopleOutlineIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Subscriptions" value={agentStats?.subscriptions || 0} icon={<HubIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Subscribers" value={agentStats?.subscribers || 0} icon={<DynamicFeedIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="Data Sources" value={agentStats?.dataSources || 0} icon={<StorageIcon />} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2.4 }}>
          <StatCard title="User Apps" value={agentStats?.userApps || 0} icon={<DevicesIcon />} />
        </Grid>

        {agentStats?.dataSourceItemStats && (
          <>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Total Items"
                value={agentStats?.dataSourceItemStats?.total || 0}
                icon={<StorageIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Processed"
                value={agentStats?.dataSourceItemStats?.processed || 0}
                icon={<CheckCircleOutlineIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Unprocessed"
                value={agentStats?.dataSourceItemStats?.unprocessed || 0}
                icon={<TimelineIcon />}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                title="Failed"
                value={agentStats?.dataSourceItemStats?.number || 0}
                icon={<ErrorOutlineIcon />}
                subtitle={
                  agentStats?.dataSourceItemStats?.lastProcessed
                    ? `Last: ${agentStats.dataSourceItemStats.lastProcessed}`
                    : undefined
                }
              />
            </Grid>
          </>
        )}
      </Grid>

      <Box sx={{ flex: 1, minHeight: 400 }}>
        <EntityLlmUsages entity={agentInstance} />
      </Box>
    </Box>
  );
};

export default AgentUsageStats;
